/**
 * WorklistScreen — the biologiste sign-off worklist (VAL-1 / D-15). Lists brut/repris results
 * (criticals first, server-ordered), supports multi-select + batch sign-off (F8), single sign-off,
 * and surfaces D-24 corrections in their own tab. Every mutating action is gated on
 * `validation.resultat.signoff`; the screen itself is reachable only with `validation.resultat.read`
 * (module-level gate). Implements all 5 states: loading/error/empty via <Table>, offline + forbidden
 * explicitly.
 */
import { useMemo, useState } from "react";
import { Badge, Button, Card, ForbiddenState, OfflineState, Table, useToast, type Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import type { ApiError } from "@/api";
import { token } from "@/design/theme";
import {
  CAP,
  isCorrection,
  isCritical,
  isSansReference,
  resultValue,
  type Decision,
  type SignItem,
  type WorklistResultat,
} from "../types";
import { useOpenedEscalades, useSignBatch, useSignOne, useWorklist } from "../hooks/useValidation";
import { useHotkey } from "../hooks/useHotkey";
import { FlagBadge, ResultStatusBadge } from "../components/Badges";
import { SignOffModal } from "../components/SignOffModal";

type TabId = "all" | "critical" | "corrections";

export function WorklistScreen() {
  const { t } = useT();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canSign = useCan(CAP.signoff);

  const { data, isLoading, isError, error, refetch } = useWorklist();
  const signBatch = useSignBatch();
  const signOne = useSignOne();
  const rememberEscalade = useOpenedEscalades((s) => s.remember);

  const [tab, setTab] = useState<TabId>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modalRows, setModalRows] = useState<WorklistResultat[] | null>(null);

  const rows = useMemo(() => data ?? [], [data]);
  const criticalCount = useMemo(() => rows.filter(isCritical).length, [rows]);
  const correctionCount = useMemo(() => rows.filter(isCorrection).length, [rows]);

  const visibleRows = useMemo(() => {
    if (tab === "critical") return rows.filter(isCritical);
    if (tab === "corrections") return rows.filter(isCorrection);
    return rows;
  }, [rows, tab]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(String(r.id))),
    [rows, selected],
  );
  const selectedCriticalCount = useMemo(() => selectedRows.filter(isCritical).length, [selectedRows]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((r) => selected.has(String(r.id)));
  const toggleAllVisible = () =>
    setSelected((s) => {
      const next = new Set(s);
      if (allVisibleSelected) visibleRows.forEach((r) => next.delete(String(r.id)));
      else visibleRows.forEach((r) => next.add(String(r.id)));
      return next;
    });

  const openBatch = () => {
    if (!canSign || selectedRows.length === 0) return;
    setModalRows(selectedRows);
  };
  const openSingle = (r: WorklistResultat) => {
    if (!canSign) return;
    setModalRows([r]);
  };

  // F8 → sign the current selection (keyboard-first). Disabled while a modal is open or empty.
  useHotkey("F8", openBatch, canSign && selectedRows.length > 0 && modalRows === null);

  const submitting = signBatch.isPending || signOne.isPending;

  const handleConfirm = async (input: {
    decision: Decision;
    commentaire: string;
    acks: Record<string, boolean>;
    acksSansRef: Record<string, boolean>;
  }) => {
    if (!modalRows) return;
    const items: SignItem[] = modalRows.map((r) => ({
      resultat_id: r.id,
      decision: input.decision,
      ...(isCritical(r) ? { acquittement_critique: input.acks[String(r.id)] === true } : {}),
      ...(isSansReference(r) ? { acquittement_sans_reference: input.acksSansRef[String(r.id)] === true } : {}),
      ...(input.commentaire ? { commentaire: input.commentaire } : {}),
    }));

    try {
      const outcomes =
        items.length === 1
          ? [
              await signOne.mutateAsync({
                id: items[0]!.resultat_id,
                ...(items[0]!.decision ? { decision: items[0]!.decision } : {}),
                ...(items[0]!.acquittement_critique !== undefined
                  ? { acquittement_critique: items[0]!.acquittement_critique }
                  : {}),
                ...(items[0]!.acquittement_sans_reference !== undefined
                  ? { acquittement_sans_reference: items[0]!.acquittement_sans_reference }
                  : {}),
                ...(items[0]!.commentaire ? { commentaire: items[0]!.commentaire } : {}),
              }),
            ]
          : await signBatch.mutateAsync({ items });

      const opened = outcomes.filter((o) => o.escalade_id != null);
      opened.forEach((o) =>
        rememberEscalade({ escaladeId: o.escalade_id!, resultatId: o.resultat_id, openedAt: Date.now() }),
      );

      if (opened.length > 0) {
        toast.warn(
          t("validation.sign.okEscalade", { n: outcomes.length, e: opened.length }),
        );
      } else {
        toast.success(t("validation.sign.ok", { n: outcomes.length }));
      }
      setSelected(new Set());
      setModalRows(null);
    } catch (e) {
      const err = e as ApiError;
      if (err.code === "VALIDATION_ACQUITTEMENT_CRITIQUE_REQUIS") {
        const ids = (err.details as { resultat_ids?: string[] } | undefined)?.resultat_ids;
        toast.error(t("validation.sign.ackRequired", { n: ids?.length ?? selectedCriticalCount }));
      } else if (err.code === "CONFLICT") {
        toast.error(t("validation.sign.conflict"));
        void refetch();
      } else {
        toast.error(err.message || t("validation.sign.error"));
      }
    }
  };

  // ── 5-state DoD ──────────────────────────────────────────────────────────────
  // Forbidden (module read) is handled by ProtectedRoute; offline is screen-level.
  if (!online && !data) return <OfflineState onRetry={() => void refetch()} cached={false} />;

  const columns: Column<WorklistResultat>[] = [
    {
      key: "select",
      header: (
        <input
          type="checkbox"
          aria-label={t("validation.action.selectAll")}
          checked={allVisibleSelected}
          onChange={toggleAllVisible}
          disabled={!canSign || visibleRows.length === 0}
        />
      ),
      width: "48px",
      cell: (r) => (
        <input
          type="checkbox"
          aria-label={`${t("validation.col.select")} #${String(r.id)}`}
          checked={selected.has(String(r.id))}
          onChange={() => toggle(String(r.id))}
          onClick={(e) => e.stopPropagation()}
          disabled={!canSign}
        />
      ),
    },
    {
      key: "id",
      header: t("validation.col.result"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => Number(r.id),
      cell: (r) => (
        <span className="nv-mono nv-ltr-island">
          #{String(r.id)}
          {isCorrection(r) && (
            <Badge tone="warn" style={{ marginInlineStart: token.s2 }}>
              {t("validation.correction.version", { n: r.version ?? 1 })}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "value",
      header: t("validation.col.value"),
      mono: true,
      align: "end",
      cell: (r) => (
        <span className="nv-mono nv-ltr-island" style={{ fontWeight: 600 }}>
          {resultValue(r)}
        </span>
      ),
    },
    {
      key: "unit",
      header: t("validation.col.unit"),
      mono: true,
      cell: (r) => <span className="nv-mono nv-ltr-island">{r.unite ?? "—"}</span>,
    },
    {
      key: "flag",
      header: t("validation.col.flag"),
      sortable: true,
      sortAccessor: (r) => (isCritical(r) ? 0 : 1),
      cell: (r) => <FlagBadge drapeau={r.drapeau} />,
    },
    {
      key: "status",
      header: t("validation.col.status"),
      cell: (r) => <ResultStatusBadge statut={r.statut} isCorrection={isCorrection(r)} />,
    },
    {
      key: "actions",
      header: "",
      align: "end",
      cell: (r) =>
        canSign ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              openSingle(r);
            }}
          >
            {t("validation.action.signOne")}
          </Button>
        ) : null,
    },
  ];

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: "all", label: t("validation.tab.all"), count: rows.length },
    { id: "critical", label: t("validation.tab.critical"), count: criticalCount },
    { id: "corrections", label: t("validation.tab.corrections"), count: correctionCount },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <header style={{ display: "flex", flexDirection: "column", gap: token.s1 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: token.s3, flexWrap: "wrap" }}>
          <h1 style={{ fontSize: token.fsH1, margin: 0 }}>{t("validation.worklist.title")}</h1>
          {!isLoading && !isError && (
            <Badge tone="info">{t("validation.worklist.count", { n: rows.length })}</Badge>
          )}
          {criticalCount > 0 && <Badge tone="critical">{criticalCount}</Badge>}
        </div>
        <span style={{ color: token.slate, fontSize: token.fsLabel }}>{t("validation.subtitle")}</span>
      </header>

      {/* Filter tabs — lightweight segmented control (the Tabs primitive is for tabpanels). */}
      <div role="tablist" aria-label={t("validation.worklist.title")} style={{ display: "flex", gap: token.s2 }}>
        {tabs.map((tb) => {
          const isCorr = tb.id === "corrections";
          return (
            <button
              key={tb.id}
              type="button"
              role="tab"
              aria-selected={tab === tb.id}
              onClick={() => setTab(tb.id)}
              className="nv-btn nv-btn--sm"
              style={{
                background: tab === tb.id ? token.azureSoft : token.surface,
                color: tab === tb.id ? token.azure600 : token.slate,
                border: `1px solid ${tab === tb.id ? token.azure : token.line}`,
              }}
            >
              {tb.label}
              <span
                className="nv-mono nv-ltr-island"
                style={{ marginInlineStart: token.s2, color: isCorr && tb.count > 0 ? token.warn : "inherit" }}
              >
                {tb.count}
              </span>
            </button>
          );
        })}
      </div>

      {tab === "corrections" && (
        <Card>
          <p style={{ margin: 0, color: token.slate, fontSize: token.fsLabel }}>
            {t("validation.correction.desc")}
          </p>
        </Card>
      )}

      {/* Selection action bar — sticky affordance for the batch gesture. */}
      {canSign ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: token.s3,
            flexWrap: "wrap",
            padding: token.s3,
            borderRadius: token.rMd,
            border: `1px solid ${token.line}`,
            background: token.surface2,
          }}
        >
          <strong>{t("validation.selection.count", { n: selectedRows.length })}</strong>
          {selectedCriticalCount > 0 && (
            <Badge tone="critical">{t("validation.selection.criticals", { n: selectedCriticalCount })}</Badge>
          )}
          <span style={{ marginInlineStart: "auto", display: "flex", gap: token.s2, flexWrap: "wrap" }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
              disabled={selectedRows.length === 0}
            >
              {t("validation.action.clear")}
            </Button>
            <Button onClick={openBatch} disabled={selectedRows.length === 0} title={t("validation.shortcut.sign")}>
              {t("validation.action.signSelected")}
              <kbd
                style={{
                  marginInlineStart: token.s2,
                  fontSize: token.fsCaption,
                  border: `1px solid ${token.lineSoft}`,
                  borderRadius: token.rXs,
                  padding: `0 ${token.s1}`,
                }}
              >
                F8
              </kbd>
            </Button>
          </span>
        </div>
      ) : (
        <ForbiddenState
          title={t("nav.validation")}
          requiredCapabilities={[CAP.signoff]}
        />
      )}

      <Table<WorklistResultat>
        columns={columns}
        rows={visibleRows}
        rowKey={(r) => String(r.id)}
        caption={t("validation.worklist.title")}
        loading={isLoading}
        error={isError ? { message: error?.message ?? "", onRetry: () => void refetch() } : null}
        emptyTitle={
          tab === "corrections" ? t("validation.correction.empty.title") : t("validation.worklist.empty.title")
        }
        emptyDescription={
          tab === "corrections" ? t("validation.correction.empty.desc") : t("validation.worklist.empty.desc")
        }
        {...(canSign ? { onRowClick: (r: WorklistResultat) => openSingle(r) } : {})}
        pageSize={25}
      />

      {modalRows && (
        <SignOffModal
          open={modalRows !== null}
          onClose={() => !submitting && setModalRows(null)}
          rows={modalRows}
          onConfirm={handleConfirm}
          submitting={submitting}
        />
      )}
    </div>
  );
}
